import { ErrorInfo } from "./http-models";

/** Modelo de requests de mensagens de assinatura digital */
export interface CoreRequestModel {
    id: string;
    type: string;
    operation: string;
    computeStatistics?: boolean;
    arguments?: any;
}

/** Modelo de resposta de mensagens ao core de assinatura digital
 */
export interface CoreResponseModel {
    id: string;
    type: string;
    operation: string;
    hasNext?: boolean;
    error?: ErrorInfo;
    result?: any;
    statistics?: any;
}

export interface CertificateInfo {
    certID: string;
    subjectDN: string;
    issuerDN: string;
    serial: string;
    isValid: boolean;
    hasKey: boolean;
    notBefore: Date;
    notAfter: Date;
    keyStoreType: string;
    certChain?: string[];
    userData?: { [key: string] : any };
}

export interface InstalledVersionInfo
{
    hostVersion: string;
    coreVersion: string;
    hostUpdate?: {
        required: boolean;
        version: string;
    };
}

export interface RequiredVersionInfo
{
    applet?: string;
    chrome?: string;
    gecko?: string;
    host?: string;
}

export interface CheckVersionResult
{
    host?: CheckVersionResultItem;
    core?: CheckVersionResultItem;
}

export interface CheckVersionResultItem
{
    required: boolean;
    version: string;
}

export interface GenericError {
    type: string;
    msg: string;
}
export interface GenericErrorResultModel {
    error: GenericError;
}

export class VersionCheckError
{
    /** Falha ao consultar versão necessária da extensão
     */
    static ERR_TYPE_QUERY_FAILURE     = 1;
    /** Módulo nativo necessita atualização
     */
    static ERR_TYPE_HOST_NEEDS_UPDATE = 2;
    /** Extensão do chrome necessita atualização
     */
    static ERR_TYPE_VERSION_MISMATCH  = 3;
    /** Módulo nativo não foi possível de ser contatado
     */
    static ERR_TYPE_HOST_UNREACHABLE  = 4;

    checkErrorType: number;
    msg: string;
    type: string;
    requiredVersion?: string;
    stacktrace?: string;

    constructor(checkErrorType:number, errorInfo:ErrorInfo, requiredVersion?:string)
    {
        this.checkErrorType  = checkErrorType;
        this.msg             = errorInfo.msg;
        this.type            = errorInfo.type || "VersionCheckError";
        this.stacktrace      = errorInfo.stacktrace;
        this.requiredVersion = requiredVersion;
    }
    /** Retorna um novo erro de validação de versão, dizendo que o host nativo precisa de update
     * @param requiredVersion Versão requerida
     * @returns Erro de verificação de versão com as informações adequadas
     */
    static hostNeedsUpdate(requiredVersion: string) : VersionCheckError {
        return new VersionCheckError(
            VersionCheckError.ERR_TYPE_HOST_NEEDS_UPDATE,
            { msg: "Assinador digital (Módulo Nativo) necessita de atualização", type: "VersionCheckError" },
            requiredVersion
        );
    }
    /** Retorna um novo erro de validação de versão, dizendo que o host nativo está unreachable
     * @param requiredVersion Versão requerida
     * @param errorInfo Informações de erro retornadas pela extensão
     * @returns Erro de verificação de versão com as informações adequadas
     */
    static unreachableHost(requiredVersion: string, errorInfo:ErrorInfo) : VersionCheckError {
        return new VersionCheckError(
            VersionCheckError.ERR_TYPE_HOST_UNREACHABLE,
            errorInfo,
            requiredVersion
        );
    }
    /** Retorna um novo erro de validação de versão, dizendo que a extensão necessita de update
     * @param requiredVersion Versão requerida
     * @returns Erro de verificação de versão com as informações adequadas
     */
    static extNeedsUpdate(requiredVersion: string) : VersionCheckError {
        return new VersionCheckError(
            VersionCheckError.ERR_TYPE_VERSION_MISMATCH,
            { msg: "Extensão do navegador necessita de atualização", type: "VersionCheckError" },
            requiredVersion
        );
    }
}

class VersionStruct
{
    static parse(versionStr: string) : VersionStruct {
        //verifica se informou version string
        if (!versionStr) throw new Error("versionStr não foi informada");

        //faz split e valida formato da versão
        const splitted: string[] = versionStr.split(".");
        if (!splitted || splitted.length < 3) throw new Error("versionStr não possui formato de 3 valores");

        //faz parsing para number e constrói objeto
        return new VersionStruct(parseInt(splitted[0], 10), parseInt(splitted[1], 10), parseInt(splitted[2], 10));
    }

    static max(a: VersionStruct, b: VersionStruct) : VersionStruct {
        return b.isHigherThan(a) ? b : a;
    }

    static maxVersionString(v1: string, v2: string) : VersionStruct {
        const a = VersionStruct.parse(v1);
        const b = VersionStruct.parse(v2);
        return VersionStruct.max(a, b);
    }

    constructor(
        public major: number,
        public minor: number,
        public patch: number
    ) { }

    isHigherThan(other: VersionStruct) : boolean {
        //bate diferença nas majors
        if (this.major > other.major) return true;
        if (this.major < other.major) return false;
        //majors são iguais... bate diferença nas minors
        if (this.minor > other.minor) return true;
        if (this.minor < other.minor) return false;
        //majors e minor iguais... vamos bater os patches
        if (this.patch > other.patch) return true;
        return false;
    }

    toString() : string {
        return `${this.major}.${this.minor}.${this.patch}`;
    }
}

export function detectExtensionType() : string {
    const browserName = window.navigator.userAgent;

    if (!(/\sedge\/[\d\.]+/i).test(browserName)) {
        if ((/\schrome\/[\d\.]+\s/i).test(browserName)) return CoreConstants.EXT_TYPE_CHROME;
        if ((/\sgecko\/[\d]+\s/i).test(browserName)) return CoreConstants.EXT_TYPE_GECKO;
    }
    
    return CoreConstants.EXT_TYPE_APPLET;
}

/**
 * Verifica se o client possui instalada a versão mínima necessária para utiliza a assinatura digital
 * @param installed  Versão instalada atualmente no client
 * @param required   Versão necessária para utilizar assinatura digital
 * @param [thisPlatform] Plataforma atual onde função está sendo executada (ex: gecko, chrome, applet)
 * @returns          Modelo indicando qual é a última versão e se a atualização é obrigatória. Retorna null se estiver atualizada. 
 */
export function checkVersion(
    installed     : InstalledVersionInfo,
    required      : RequiredVersionInfo,
    thisPlatform? : string
): CheckVersionResult {
    //se não tem resource dizendo o que precisamos, vamos retornar que está tudo bem
    if (!required) return null;

    //objeto de resultado e flag indicando se encontramos alguma discrepância
    let result: CheckVersionResult = { };
    let problems: boolean = false;

    //objeto contendo chaves atuais de problema (esse array serve para que um problema para a key 'gecko' seja replicada em 'extension')
    const problemKeys:string[] = [];
    const addProblem = function(p: CheckVersionResultItem) {
        //vincula problema as chaves de problema indicadas e flaga que temos problemas
        for (const k of problemKeys) result[k] = p;
        problems = true;
    };

    //percorre cada versão que o usuário quer validar
    for (let key in installed) {
        //somente strings nos interessam
        if (typeof(installed[key]) !== "string") continue;

        //pega valor da versão disponível
        const availVersion: string = installed[key];

        //existem chaves que possuem "Version" como sufixo, remove tal sufixo. Busca então a versão bindada à library
        key = key.replace(/Version$/, "");
        problemKeys.splice(0, problemKeys.length, key);
        
        // se for extension muda key para a extensão que deve ser utilizada
        if (key === "extension") {
            key = thisPlatform || detectExtensionType();
            problemKeys.push(key);
        }
        const reqVersion: string = required[key];

        //verifica se tem as 2 informações para comparar
        if (reqVersion && availVersion) {
            //splita versões pelo separador
            const parsedRequired  = VersionStruct.parse(reqVersion); //  string[] = reqVersion.split(".");
            const parsedAvailable = VersionStruct.parse(availVersion);  //: string[] = availVersion.split(".");

            //se a major version é diferente, temos um problema de incompatibilidade forte
            if (parsedRequired.major !== parsedAvailable.major) {
                addProblem({ required: true, version: reqVersion });
                continue;
            }

            //se precisar de uma minorVersion mais recente, a funcionalidade que queremos pode não estar implementada: atualização obrigatória também
            if (parsedRequired.minor > parsedAvailable.minor) {
                addProblem({ required: true, version: reqVersion });
                continue;
            }

            //caso apenas a PATCH requerida seja maior, vamos dizer que a atualização é recomendável
            if (parsedRequired.minor === parsedAvailable.minor && parsedRequired.patch > parsedAvailable.patch) {
                addProblem({ required: false, version: reqVersion });
                continue;
            }
        }
    }

    //retorna problemas, se encontrou algum
    return problems ? result : null;
}

export function combineRequirements(current: RequiredVersionInfo, newReqs: RequiredVersionInfo) : RequiredVersionInfo
{
    const maxVerStr = VersionStruct.maxVersionString;

    return {
        host   : newReqs.host ? maxVerStr(current.host, newReqs.host).toString() : current.host,
        applet : newReqs.applet ? maxVerStr(current.applet, newReqs.applet).toString() : current.applet,
        chrome : newReqs.chrome ? maxVerStr(current.chrome, newReqs.chrome).toString() : current.chrome,
        gecko  : newReqs.gecko ? maxVerStr(current.gecko, newReqs.gecko).toString() : current.gecko,
    };
}

export function checkWebExtRuntimeVersions(resp: CoreResponseModel, versionBindings: RequiredVersionInfo, thisPlatform? : string) : Promise<CoreResponseModel>
{
    //veio erro direto da extensão
    if (resp.error)
    {
        const fail:ErrorInfo = resp.error;
        switch (fail.type)
        {
            case "web_error.hostUnreachable":
            case "chrome_error.hostUnreachable": {
                //escolhe maior versão entre a pedida pela extensão e a pedida pela app
                const extReq:string = (<any>fail).data.requiredHostVersion;
                const higher:string = VersionStruct.maxVersionString(extReq, versionBindings.host).toString();
                //retorna rejeição
                return Promise.reject(VersionCheckError.unreachableHost(higher, fail));
            }
            case "web_error.needsHostUpdate":
            case "chrome_error.needsHostUpdate": {
                //escolhe maior versão entre a pedida pela extensão e a pedida pela app
                const extReq:string = (<any>fail).data.requiredHostVersion;
                const higher:string = VersionStruct.maxVersionString(extReq, versionBindings.host).toString();
                //mantém tipo de erro vindo da response model
                let rj = VersionCheckError.hostNeedsUpdate(higher);
                rj.type = fail.type;
                return Promise.reject(rj);
            }
            default:
                return Promise.reject(new VersionCheckError(VersionCheckError.ERR_TYPE_QUERY_FAILURE, fail));
        }
    }

    //se não for erro porém tiver chave "hostUpdate", vamos ver se o update não é required
    const extHostUpdate = resp.result.hostUpdate;
    if (extHostUpdate && extHostUpdate.required) {
        //escolhe maior versão entre a pedida pela extensão e a pedida pela app
        const extReq:string = extHostUpdate.version;
        const higher:string = VersionStruct.maxVersionString(extReq, versionBindings.host).toString();

        return Promise.reject(VersionCheckError.hostNeedsUpdate(higher));
    }

    //se chegou até aqui, quer dizer que a extensão está feliz, porém vamos ter que bater os bindings da app também
    const versionCheck = checkVersion(resp.result, versionBindings, thisPlatform);
    if (versionCheck) {
        //se requer update no host (por definição da library js), vamos reportar isso
        const hostUpdate:CheckVersionResultItem = versionCheck["host"];
        if (hostUpdate && hostUpdate.required) {
            return Promise.reject(VersionCheckError.hostNeedsUpdate(hostUpdate.version));
        }
        //se precisa de update na extensão
        const extUpdate:CheckVersionResultItem = versionCheck["extension"];
        if (extUpdate && extUpdate.required) {
            return Promise.reject(VersionCheckError.extNeedsUpdate(extUpdate.version));
        }
    }

    return Promise.resolve(resp);
}

/** Classe para agrupar constantes relevantes para o core de assinatura
 */
export class CoreConstants
{
    /** Protocolo de mensagem de requisição de assinatura */
    static readonly REQUEST_MSG_TYPE: string = "qs.signer.request";
    /** Protocolo de mensagem de resposta de assinatura */
    static readonly RESPONSE_MSG_TYPE: string = "qs.signer.response";
    /** Protocolo de proxy de mensagem de requisição de assinatura */
    static readonly PROXY_REQUEST_MSG_TYPE: string = "qs.signer.proxy.request";
    /** Protocolo de proxy de mensagem de resposta de assinatura */
    static readonly PROXY_RESPONSE_MSG_TYPE: string = "qs.signer.proxy.response";

    /** Operação de ping */
    static readonly OP_EXT_PING: string = "EXT_PING";
    /** Operação de finalização de sessão de assinatura */
    static readonly OP_EXT_RELEASE_SESSION: string = "EXT_RELEASE_SESSION";
    /** Operação de consulta de versão */
    static readonly OP_EXT_VERSION: string = "EXT_VERSION";
    /** Operação de consulta de certificados */
    static readonly OP_LIST_CERTS: string = "LIST_CERTS";
    /** Operação de obtenção de cadeia de certificados */
    static readonly OP_GET_CERT_CHAIN: string = "GET_CERT_CHAIN";
    /** Operação de assinatura */
    static readonly OP_SIGN: string = "SIGN";
    /** Operação de consulta de versão */
    static readonly OP_CANCEL: string = "CANCEL";

    static readonly EXT_TYPE_APPLET = "applet";
    static readonly EXT_TYPE_CHROME = "chrome";
    static readonly EXT_TYPE_GECKO = "gecko";

    /** Constante de nome do erro de java applet não suportado */
    static readonly ERR_JAVA_APPLET_NOT_SUPPORTED = "qs.err.javaAppletNotSupported";
    /** Constante de nome do erro de plataforma não suportada */
    static readonly ERR_PLATFORM_NOT_SUPPORTED = "qs.err.platformNotSupported";

    /** Id de sessão java compartilhada */
    static readonly SHARED_JAVA_SESSION_ID: string = "shared";
}

/** Classe para geração de id de requisição
 */
export class RequestIdGenerator
{
    /** Id da origem da requisição
     */
    private readonly requestSourceId: string;
    /** Número sequencial do último id gerado
     */
    private sequence: number;

    /** Construtor
     * @param requestSourceId  Id da origem da requisição
     */
    constructor(requestSourceId: string) {
        this.requestSourceId = requestSourceId;
        this.sequence = 0;
    }

    /** Gera próximo id de requisição
     * @param javaSessionId  Id da sessão java
     * @returns              Próximo id para a requisição de assinatura
     */
    next(javaSessionId: string): string {
        const seq = ++this.sequence;
        return javaSessionId + "@" + this.requestSourceId + "|" + seq;
    }

    /** Extrai o id da sessão java do id da requisição
     * @param requestId  Id da requisição de assinatura
     * @returns          Id da sessão java
     */
    extractJavaSessionId(requestId: string) : string {
        return requestId.split("@")[0];
    }
}





// WEBPACK FOOTER //
// ./lib/common.ts